
    import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
    import { errorSwal} from '@/helper/swals';
    import { mapActions, mapGetters, mapState } from 'vuex';
    import moment from 'moment';
    import Lagerbuchung from '@/types/Lagerbuchung';
    import Charge from '@/types/Charge';
    import { ValidationRules } from '@/store/types';
    import { VAutocomplete, VForm, VTextField } from '@/types/Components';
    import Impfstoff from '@/types/Impfstoffe';
    import { BuchungFilter, LoadChargenByImpfstoffIdPayload, NeueBuchungPayload } from '@/store/lagerfuehrung';
    import { DataTableHeaders } from '@/types';

    @Component({
        computed: {
            ...mapState('globals', ['rules', 'formatDate', 'parseDate']),
            ...mapState('patient', ['patientMitAdresse', 'patientDataNew', 'patientData']),
            ...mapState('lagerfuehrung', ['loaders', 'filters', 'selected_impfstoff', 'impfstoffchargen']),
            ...mapState('authentication', ['impfstelle_id']),
            ...mapGetters('lagerfuehrung', ['getImpfstoff_id', 'lagerbuchungsarten']),
        },
        methods: {
            ...mapActions('lagerfuehrung', [
                'dialogBuchung',
                'neueBuchung',
                'loadChargenByImpfstoffId',
                'updateCharge',
                'loadLagerbuchungsarten',
            ]),
        },
    })
    export default class Buchung extends Vue {
        @Prop() visible!: boolean;

        public $refs!: {
            form: VForm;
            buchungsart: VAutocomplete;
            eingangsdatum: VTextField;
            impfstoffchargeId: VAutocomplete;
            chargennr: VTextField;
            lagerbestand: VTextField;
        };

        public rules!: ValidationRules;
        public formatDate!: (input: string | null) => string | null;
        public parseDate!: (input: string | null) => string | null;
        public loadLagerbuchungsarten!: () => void;
        public loadChargenByImpfstoffId!: (payload?: LoadChargenByImpfstoffIdPayload) => void;
        public updateCharge!: (charge: Charge) => Promise<boolean>;
        public getImpfstoff_id!: number;
        public impfstelle_id!: number;
        public selected_impfstoff!: Impfstoff | null;
        public impfstoffchargen!: Charge[];
        public neueBuchung!: (payload: NeueBuchungPayload) => Promise<boolean>;

        public buchung = new Lagerbuchung();
        public charge = new Charge();

        public filter: BuchungFilter = {
            verfuegbar: 'j',
        };

        public isEdit = false;
        public aktuellerLagerbestandCharge = 0;

        @Watch('visible')
        public onChangeVisible(): void {
            if (this.visible) {
                this.reset();
            }
        }

        public reset(): void {
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }

            this.loadLagerbuchungsarten();

            this.buchung = new Lagerbuchung();
            this.charge = new Charge();

            this.aktuellerLagerbestandCharge = 0;
            this.isEdit = false;
            this.buchung.impfstoff_id = this.getImpfstoff_id;
            this.buchung.impfstelle_id = this.impfstelle_id;

            this.charge.eingangsdatum = moment().format('YYYY-MM-DD');
            this.charge.lagerbestand = 0;
            this.charge.impfstelle_id = this.impfstelle_id;
            this.charge.impfstoff_id = this.getImpfstoff_id;

            this.onChangeLagerbuchungsart();

            this.loadChargenByImpfstoffId({ filter: this.filter });

            this.eingangsdatumErrorMessages = [];
            this.eingangsdatumFormatted = this.formatDate(this.charge.eingangsdatum);
            this.ablaufdatumErrorMessages = [];
            this.ablaufdatumFormatted = this.formatDate(this.charge.ablaufdatum);
            this.lieferscheindatumErrorMessages = [];
            this.lieferscheindatumFormatted = this.formatDate(this.charge.lieferscheindatum);
            this.rechnungsdatumErrorMessages = [];
            this.rechnungsdatumFormatted = this.formatDate(this.charge.rechnungsdatum);
        }

        public get headers(): DataTableHeaders<Charge> {
            return [
                { text: '', value: 'actions', sortable: false, width: 50 },
                { text: 'Chargen Nr.', value: 'chargennr', sortable: false },
                { text: 'Eingangsdatum', value: 'eingangsdatum', sortable: false },
                { text: 'Ablaufdatum', value: 'ablaufdatum', sortable: false },
                { text: 'Einkaufspreis', value: 'einkaufspreis', sortable: false },
                { text: 'Lagerbestand', value: 'lagerbestand', sortable: false },
                { text: 'Anmerkung', value: 'anmerkung', sortable: false },
            ];
        }

        public get title(): string {
            return this.isEdit
                ? 'Charge bearbeiten'
                : `Lagerbuchung für ${this.selected_impfstoff?.bezeichnung ?? ''}`;
        }

        public get isZugang(): boolean {
            return this.buchung.lagerbuchungsart === 'Zugang';
        }

        public onChangeVerfuegbareChargen(): void {
            this.loadChargenByImpfstoffId({ filter: this.filter });

            if (this.$refs.impfstoffchargeId) {
                this.$refs.impfstoffchargeId.reset();
            }
        }

        public onChangeLagerbuchungsart(): void {
            if (this.$refs.lagerbestand) {
                this.$refs.lagerbestand.resetValidation();
            }

            if (this.buchung.lagerbuchungsart === 'Abgang' || this.buchung.lagerbuchungsart === 'Bruch') {
                this.charge.lagerbestand = -1;
            } else {
                this.charge.lagerbestand = 1;
            }

            if (this.buchung.lagerbuchungsart === 'Zugang') {
                this.charge.impfstoffcharge_id = null;

                if (this.$refs.impfstoffchargeId) {
                    this.$refs.impfstoffchargeId.reset();
                }
            } else {
                this.charge.chargennr = null;

                if (this.$refs.chargennr) {
                    this.$refs.chargennr.reset();
                }

                if (this.impfstoffchargen.length === 1) {
                    this.charge.impfstoffcharge_id = this.impfstoffchargen[0].impfstoffcharge_id;
                }
            }
        }

        @Watch('loaders.charge')
        public onChangeLoadersCharge(loading: boolean): void {
            if (!loading) {
                this.isEdit = false;
            }
        }

        @Watch('charge.impfstoffcharge_id')
        public onChangeImpfstoffchargeId(): void {
            const charge = this.impfstoffchargen.find((charge: Charge) => {
                return charge.impfstoffcharge_id === this.charge.impfstoffcharge_id;
            });

            if (charge) {
                this.aktuellerLagerbestandCharge = charge.lagerbestand ?? 0;
                this.charge.eingangsdatum = this.parseDate((charge.eingangsdatum?.split(' ') ?? [])[0] ?? '');
                this.charge.ablaufdatum = this.parseDate((charge.ablaufdatum?.split(' ') ?? [])[0] ?? '');
                this.charge.lieferant = charge.lieferant;
                this.charge.lieferscheinnr = charge.lieferscheinnr;
                this.charge.lieferscheindatum = this.parseDate((charge.lieferscheindatum?.split(' ') ?? [])[0] ?? '');
                this.charge.einkaufspreis = charge.einkaufspreis;
                this.charge.rechnungsdatum = this.parseDate((charge.rechnungsdatum?.split(' ') ?? [])[0] ?? '');
            }
        }

        public get aktuellerLagerbestand(): number {
            const lagerbestandGesamt = parseInt(this.selected_impfstoff?.lagerbestand ?? '0', 10);
            return !isNaN(lagerbestandGesamt) ? lagerbestandGesamt : 0;
        }

        public get neuerLagerbestand(): number {
            if (this.isEdit) {
                return this.aktuellerLagerbestand;
            }

            return this.aktuellerLagerbestand + (this.charge.lagerbestand ?? 0);
        }

        public get neuerLagerbestandCharge(): number {
            if (this.isEdit) {
                return this.aktuellerLagerbestandCharge;
            }

            return this.aktuellerLagerbestandCharge + (this.charge.lagerbestand ?? 0);
        }

        public eingangsdatumMenu = false;
        public eingangsdatumFormatted: string | null = null;
        public eingangsdatumErrorMessages: string[] = [];

        @Watch('charge.eingangsdatum')
        public onChangeEingangsdatum(date: string | null): void {
            this.eingangsdatumFormatted = this.formatDate(this.charge.eingangsdatum);

            if (date !== null) {
                this.validateEingangsdatum();
            }
        }

        public validateEingangsdatum(): void {
            this.eingangsdatumErrorMessages = [];

            if (this.isZugang && !this.isEdit) {
                for (const rule of [this.rules.required, this.rules.validDate]) {
                    const valid = rule(this.charge.eingangsdatum);
                    if (valid !== true) {
                        this.eingangsdatumErrorMessages.push(valid as string);
                    }
                }
            }
        }

        public ablaufdatumMenu = false;
        public ablaufdatumFormatted: string | null = null;
        public ablaufdatumErrorMessages: string[] = [];

        @Watch('charge.ablaufdatum')
        public onChangeAblaufdatum(date: string | null): void {
            this.ablaufdatumFormatted = this.formatDate(this.charge.ablaufdatum);

            if (date !== null) {
                this.validateAblaufdatum();
            }
        }

        public validateAblaufdatum(): void {
            this.ablaufdatumErrorMessages = [];

            if (this.isZugang && !this.isEdit) {
                for (const rule of [this.rules.required, this.rules.validDate]) {
                    const valid = rule(this.charge.ablaufdatum);
                    if (valid !== true) {
                        this.ablaufdatumErrorMessages.push(valid as string);
                    }
                }
            }
        }

        public lieferscheindatumMenu = false;
        public lieferscheindatumFormatted: string | null = null;
        public lieferscheindatumErrorMessages: string[] = [];

        @Watch('charge.lieferscheindatum')
        public onChangeLieferscheindatum(date: string | null): void {
            this.lieferscheindatumFormatted = this.formatDate(this.charge.lieferscheindatum);

            if (date !== null && date !== '') {
                this.validateLieferscheindatum();
            }
        }

        public validateLieferscheindatum(): void {
            this.lieferscheindatumErrorMessages = [];

            if (this.charge.lieferscheindatum && this.isZugang && !this.isEdit) {
                for (const rule of [this.rules.validDate]) {
                    const valid = rule(this.charge.lieferscheindatum);
                    if (valid !== true) {
                        this.lieferscheindatumErrorMessages.push(valid as string);
                    }
                }
            }
        }

        public rechnungsdatumMenu = false;
        public rechnungsdatumFormatted: string | null = null;
        public rechnungsdatumErrorMessages: string[] = [];

        @Watch('charge.rechnungsdatum')
        public onChangeRechnungsdatum(date: string | null): void {
            this.rechnungsdatumFormatted = this.formatDate(this.charge.rechnungsdatum);

            if (date !== null && date !== '') {
                this.validateRechnungsdatum();
            }
        }

        public validateRechnungsdatum(): void {
            this.rechnungsdatumErrorMessages = [];

            if (this.charge.rechnungsdatum && this.isZugang && !this.isEdit) {
                for (const rule of [this.rules.validDate]) {
                    const valid = rule(this.charge.rechnungsdatum);
                    if (valid !== true) {
                        this.rechnungsdatumErrorMessages.push(valid as string);
                    }
                }
            }
        }

        public editCharge(charge: Charge): void {
            this.isEdit = true;
            Object.assign(this.charge, charge);
            this.$refs.eingangsdatum.focus();
        }

        public onClickCancel(): void {
            this.isEdit = false;
            this.onChangeLagerbuchungsart();
        }

        public onClickSave(): void {
            this.validateEingangsdatum();
            this.validateAblaufdatum();
            this.validateLieferscheindatum();
            this.validateRechnungsdatum();

            if (this.$refs.form.validate() &&
                this.eingangsdatumErrorMessages.length === 0 &&
                this.ablaufdatumErrorMessages.length === 0 &&
                this.lieferscheindatumErrorMessages.length === 0 &&
                this.rechnungsdatumErrorMessages.length === 0) {
                if ((this.buchung.lagerbuchungsart === 'Zugang' || this.buchung.lagerbuchungsart === 'Zugang alt') && this.charge.lagerbestand && this.charge.lagerbestand < 0) {
                    errorSwal('Fehler', 'Die Anzahl darf nicht kleiner als 0 sein');
                } else if (this.buchung.lagerbuchungsart === 'Abgang' && this.charge.lagerbestand && this.charge.lagerbestand > 0) {
                    errorSwal('Fehler', 'Die Anzahl darf nicht größer als 0 sein');
                } else {
                    if (this.isEdit) {
                        this.updateCharge(this.charge)
                            .then((success: boolean) => success && this.reset());
                    } else {
                        const buchung = new Lagerbuchung(this.buchung);
                        const charge = new Charge(this.charge);

                        if (this.isZugang) {
                            charge.anmerkung = buchung.anmerkung;
                        }

                        this.neueBuchung({buchung, charge})
                            .then((success: boolean) => success && this.reset());
                    }
                }
            }
        }
    }
